import { useEffect, useRef } from 'react';
// Browsers don't treat mouse-drag as a horizontal-scroll gesture, so the
// native pan that works on touch never fires on desktop. This hook wires
// pointer events directly to the scroll node and translates movement into
// scrollLeft, then snaps to the nearest interval on release.
const useDragToPanScroll = ({ scrollViewRef, enabled, snapOffsets, snapInterval, maxScroll, onDragStart, onDragEnd, }) => {
    // Hold the latest callbacks in refs so the effect doesn't need to re-attach
    // listeners every render just because the caller passed fresh closures.
    const onDragStartRef = useRef(onDragStart);
    const onDragEndRef = useRef(onDragEnd);
    useEffect(() => {
        onDragStartRef.current = onDragStart;
        onDragEndRef.current = onDragEnd;
    });
    useEffect(() => {
        if (!enabled)
            return undefined;
        if (snapOffsets && snapOffsets.length === 0)
            return undefined;
        if (!snapOffsets && (!snapInterval || snapInterval <= 0))
            return undefined;
        const ref = scrollViewRef.current;
        const node = ref?.getScrollableNode?.();
        if (!node)
            return undefined;
        let dragging = false;
        let startClientX = 0;
        let startScrollLeft = 0;
        let activePointerId = -1;
        let savedSnapType = '';
        const onPointerDown = (e) => {
            // Leave touch + trackpad gestures to the browser — they already work.
            if (e.pointerType !== 'mouse')
                return;
            dragging = true;
            activePointerId = e.pointerId;
            startClientX = e.clientX;
            startScrollLeft = node.scrollLeft;
            try {
                node.setPointerCapture(e.pointerId);
            }
            catch {
                // ignore — some environments throw if pointer isn't capturable
            }
            savedSnapType = node.style.scrollSnapType;
            node.style.scrollSnapType = 'none';
            node.style.cursor = 'grabbing';
            onDragStartRef.current?.();
            e.preventDefault();
        };
        const onPointerMove = (e) => {
            if (!dragging || e.pointerId !== activePointerId)
                return;
            node.scrollLeft = startScrollLeft - (e.clientX - startClientX);
            e.preventDefault();
        };
        const onPointerEnd = (e) => {
            if (!dragging || e.pointerId !== activePointerId)
                return;
            dragging = false;
            try {
                node.releasePointerCapture(e.pointerId);
            }
            catch {
                // ignore
            }
            node.style.scrollSnapType = savedSnapType;
            node.style.cursor = 'grab';
            onDragEndRef.current?.();
            let target;
            if (snapOffsets) {
                // Bucketed: snap to the nearest pre-computed offset (small array).
                const current = node.scrollLeft;
                [target] = snapOffsets;
                let bestDist = Math.abs(current - target);
                for (let i = 1; i < snapOffsets.length; i += 1) {
                    const dist = Math.abs(current - snapOffsets[i]);
                    if (dist < bestDist) {
                        bestDist = dist;
                        target = snapOffsets[i];
                    }
                }
            }
            else {
                // Uniform: snap to the nearest multiple of snapInterval, clamped.
                const snapped = Math.round(node.scrollLeft / snapInterval) *
                    snapInterval;
                target = Math.max(0, Math.min(maxScroll, snapped));
            }
            ref?.scrollTo?.({ x: target, animated: true });
        };
        node.style.cursor = 'grab';
        node.style.userSelect = 'none';
        node.addEventListener('pointerdown', onPointerDown);
        node.addEventListener('pointermove', onPointerMove);
        node.addEventListener('pointerup', onPointerEnd);
        node.addEventListener('pointercancel', onPointerEnd);
        return () => {
            node.removeEventListener('pointerdown', onPointerDown);
            node.removeEventListener('pointermove', onPointerMove);
            node.removeEventListener('pointerup', onPointerEnd);
            node.removeEventListener('pointercancel', onPointerEnd);
            node.style.cursor = '';
            node.style.userSelect = '';
            // If we tear down mid-drag, onPointerEnd never fired — restore the snap
            // type so snapping isn't permanently disabled on this node.
            if (dragging) {
                node.style.scrollSnapType = savedSnapType;
            }
        };
    }, [enabled, snapOffsets, snapInterval, maxScroll, scrollViewRef]);
};
export default useDragToPanScroll;
